import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ShieldStar, Broom, CheckCircle, XCircle, Camera, ClockClockwise, UserCirclePlus, Crown, Sun, MoonStars, X, Plus, Trash, ImageSquare
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useDialog } from "../components/Dialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { istToday } from "../lib/day";

const CATS = {
  SECURITY: { label: "Security", icon: ShieldStar, accent: "cyan" },
  HOUSEKEEPING: { label: "Housekeeping", icon: Broom, accent: "lime" },
};

export default function StaffAttendance() {
  const { notify } = useDialog();
  const { category: paramCat } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const category = (paramCat || "SECURITY").toUpperCase();
  const cat = CATS[category] || CATS.SECURITY;
  const isAdmin = user?.role === "ADMIN";
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user?.role);

  const today = istToday(); // IST, not UTC — see lib/day.js
  const [date, setDate] = useState(today);
  const [data, setData] = useState({ items: [], canMark: false, date });
  const [shiftFilter, setShiftFilter] = useState("ALL");
  const [targetStaff, setTargetStaff] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null); // base64 photo to preview
  const camInputRef = useRef(null);

  async function load() {
    const { data } = await api.get(`/api/attendance?date=${date}&category=${category}`);
    setData(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, category]);

  // Photo preview is an overlay, not a route. Push a history entry while it's open so the
  // Android hardware Back button closes the photo and stays on this list, instead of
  // popping the router back to the dashboard.
  useEffect(() => {
    if (!viewPhoto) return;
    window.history.pushState({ spModal: "photo" }, "");
    const onPop = () => setViewPhoto(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [viewPhoto]);
  // Closing via tap/X should also unwind the pushed entry so history stays consistent.
  const closePhoto = () => {
    if (window.history.state?.spModal === "photo") window.history.back();
    else setViewPhoto(null);
  };

  // Present → mandatory live photo (opens native camera). Absent → no photo.
  function markPresent(staff) {
    if (!data.canMark) return;
    setTargetStaff(staff);
    setTimeout(() => camInputRef.current?.click(), 0);
  }
  async function markAbsent(staff) {
    if (!data.canMark) return;
    try { await api.post(`/api/attendance`, { staffId: staff.id, present: false }); await load(); }
    catch (err) { notify({ title: "Couldn't mark", message: err?.response?.data?.detail || "Could not mark attendance." }); }
  }

  // Compress to ~720px JPEG with a system timestamp burned in; returns base64 (no prefix).
  function fileToB64(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const w = Math.min(720, img.width || 720);
        const ratio = w / (img.width || w);
        const h = Math.round((img.height || 540) * ratio);
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Burn in the capture timestamp (bottom-left).
        const ts = new Date().toLocaleString();
        ctx.font = "bold 18px -apple-system, sans-serif";
        const tw = ctx.measureText(ts).width;
        ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.fillRect(8, h - 32, tw + 16, 26);
        ctx.fillStyle = "#fff"; ctx.fillText(ts, 16, h - 13);
        URL.revokeObjectURL(url);
        resolve((c.toDataURL("image/jpeg", 0.7).split(",")[1]) || "");
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(""); };
      img.src = url;
    });
  }

  async function onCamFile(e) {
    const file = e.target.files?.[0]; if (e.target) e.target.value = "";
    const staff = targetStaff;
    if (!file || !staff) { setTargetStaff(null); return; }
    try {
      const b64 = await fileToB64(file);
      await api.post(`/api/attendance`, { staffId: staff.id, present: true, photoBase64: b64, photoMime: "image/jpeg" });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      notify({ title: "Couldn't mark", message: d === "photo_required" ? "A photo is required to mark Present." : d === "already_marked" ? "Already marked for today." : "Could not mark attendance." });
    }
    setTargetStaff(null);
  }

  const filtered = useMemo(() => {
    if (category !== "SECURITY" || shiftFilter === "ALL") return data.items;
    return data.items.filter((x) => x.staff.shift === shiftFilter);
  }, [data.items, shiftFilter, category]);

  const presentCount = filtered.filter((x) => x.attendance?.present).length;

  const supervisors = filtered.filter((x) => x.staff.isSupervisor);
  const members = filtered.filter((x) => !x.staff.isSupervisor);

  return (
    <div className="space-y-4 font-sans" data-testid="staff-screen">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Society staff</p>
        {/* heading + present-count in a single row */}
        <div className="flex items-baseline gap-2">
          <h1 className="font-display text-3xl font-extrabold text-white">{cat.label}</h1>
          <span className="text-sm font-semibold text-white/55">· {presentCount} of {filtered.length} present {date === today ? "today" : `on ${date}`}</span>
        </div>
        {/* category switch buttons below — Security left, Housekeeping right */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => navigate("/staff/SECURITY")} data-testid="cat-security"
            className={`flex items-center justify-center gap-1.5 rounded-2xl border py-2.5 text-[12px] font-bold uppercase tracking-wider transition-all ${category === "SECURITY" ? "border-neon-cyan/60 bg-neon-cyan/15 text-neon-cyan" : "border-white/10 bg-white/5 text-white/65"}`}>
            <ShieldStar size={15} weight="duotone" /> Security
          </button>
          <button onClick={() => navigate("/staff/HOUSEKEEPING")} data-testid="cat-housekeeping"
            className={`flex items-center justify-center gap-1.5 rounded-2xl border py-2.5 text-[12px] font-bold uppercase tracking-wider transition-all ${category === "HOUSEKEEPING" ? "border-neon-lime/60 bg-neon-lime/15 text-neon-lime" : "border-white/10 bg-white/5 text-white/65"}`}>
            <Broom size={15} weight="duotone" /> Housekeeping
          </button>
        </div>
      </div>

      <div className="card flex items-center gap-2 p-2">
        <input type="date" max={today} className="field !py-2 text-[13px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" />
        {category === "SECURITY" && (
          <select className="field !py-2 text-[13px]" value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)} data-testid="shift-filter">
            <option value="ALL" className="bg-ink-800">All shifts</option>
            <option value="DAY" className="bg-ink-800">Day shift</option>
            <option value="NIGHT" className="bg-ink-800">Night shift</option>
          </select>
        )}
        {isAdmin && (
          <button onClick={() => setAddOpen(true)} data-testid="add-staff-btn"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-neon-cyan px-3 py-2 text-[11px] font-bold text-black shadow-glow-cyan">
            <Plus size={12} weight="bold" /> Add
          </button>
        )}
      </div>

      {!data.canMark && date === today && isStaff && (
        <div className="card flex items-start gap-2 border-neon-amber/30 bg-neon-amber/[0.06] p-3 text-[12px] text-neon-amber">
          <ClockClockwise weight="duotone" size={16} className="mt-0.5 shrink-0" />
          <span>Marking is enabled only for the current day. Earlier days are read-only.</span>
        </div>
      )}

      {/* Supervisors */}
      {supervisors.length > 0 && (
        <section>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-neon-amber"><Crown size={11} weight="fill" className="-mt-0.5 mr-1 inline" /> Supervisors</p>
          <div className="space-y-2">
            {supervisors.map((row) => <StaffRow key={row.staff.id} row={row} canMark={data.canMark} onPresent={() => markPresent(row.staff)} onAbsent={() => markAbsent(row.staff)} onViewPhoto={setViewPhoto} accent={cat.accent} isAdmin={isAdmin} onRemoved={load} />)}
          </div>
        </section>
      )}

      {/* Members */}
      {members.length > 0 && (
        <section>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">Members</p>
          <div className="space-y-2">
            {members.map((row) => <StaffRow key={row.staff.id} row={row} canMark={data.canMark} onPresent={() => markPresent(row.staff)} onAbsent={() => markAbsent(row.staff)} onViewPhoto={setViewPhoto} accent={cat.accent} isAdmin={isAdmin} onRemoved={load} />)}
          </div>
        </section>
      )}

      {filtered.length === 0 && (
        <div className="card flex flex-col items-center gap-2 p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-white/55"><UserCirclePlus size={22} weight="duotone" /></div>
          <p className="font-display text-base font-semibold text-white">No {cat.label.toLowerCase()} staff yet</p>
          <p className="text-[13px] text-white/55">{isAdmin ? "Tap Add to register a member." : "Ask the admin to add staff here."}</p>
        </div>
      )}

      {/* Native camera capture for attendance photo (reliable in the app WebView). */}
      <input ref={camInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onCamFile} />

      <AnimatePresence>
        {viewPhoto && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closePhoto}>
            <button onClick={closePhoto} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white" style={{ top: "max(1rem, env(safe-area-inset-top))" }}><X size={18} weight="bold" /></button>
            <img src={`data:image/jpeg;base64,${viewPhoto}`} alt="attendance" className="max-h-[80vh] w-full max-w-md rounded-2xl object-contain" />
          </motion.div>
        )}
        {addOpen && <AddStaffSheet category={category} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function StaffRow({ row, canMark, onPresent, onAbsent, onViewPhoto, accent, isAdmin, onRemoved }) {
  const { confirm } = useDialog();
  const { staff, attendance } = row;
  const Icon = staff.category === "SECURITY" ? (staff.shift === "NIGHT" ? MoonStars : Sun) : Broom;
  const marked = !!attendance;
  const present = attendance?.present;

  async function remove() {
    if (!(await confirm({ title: "Remove personnel?", message: `${staff.name} will be removed from the roster.`, confirmText: "Remove", danger: true }))) return;
    await api.delete(`/api/society-staff/${staff.id}`); onRemoved();
  }

  return (
    <div className="card flex items-center gap-3 p-3" data-testid={`staff-row-${staff.id}`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${accent === "lime" ? "bg-neon-lime/15 text-neon-lime" : "bg-neon-cyan/15 text-neon-cyan"}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate font-display text-[14px] font-semibold text-white">{staff.name}</p>
          {staff.isSupervisor && <Crown size={11} weight="fill" className="text-neon-amber" />}
        </div>
        <p className="text-[10px] uppercase tracking-wider text-white/50">
          {staff.category}{staff.shift ? ` · ${staff.shift}` : ""}
        </p>
        {marked && attendance.markedAt && (
          <p className="text-[10px] text-white/40">marked {new Date(attendance.markedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} by {attendance.markedByName || "—"}</p>
        )}
      </div>

      {marked ? (
        // Read-only once marked. Photo icon (present only) to view the timestamped photo.
        <div className="flex items-center gap-2">
          {present ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-neon-lime/15 px-2.5 py-1 text-[11px] font-bold text-neon-lime"><CheckCircle size={13} weight="fill" /> Present</span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-neon-pink/15 px-2.5 py-1 text-[11px] font-bold text-neon-pink"><XCircle size={13} weight="fill" /> Absent</span>
          )}
          {present && attendance.photoBase64 && (
            <button onClick={() => onViewPhoto(attendance.photoBase64)} data-testid={`photo-${staff.id}`}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-white/5 text-neon-cyan hover:bg-white/10" title="View photo">
              <ImageSquare size={15} weight="fill" />
            </button>
          )}
        </div>
      ) : canMark ? (
        // Not yet marked → Present (camera) / Not Present.
        <div className="flex items-center gap-1.5">
          <button onClick={onPresent} data-testid={`present-${staff.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-neon-lime px-2.5 py-1.5 text-[11px] font-bold text-black shadow-glow-lime">
            <Camera size={13} weight="fill" /> Present
          </button>
          <button onClick={onAbsent} data-testid={`absent-${staff.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] font-bold text-white/70 hover:bg-white/10">
            Absent
          </button>
        </div>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-bold text-white/45">Not marked</span>
      )}

      {isAdmin && !marked && (
        <button onClick={remove} className="rounded-full bg-white/5 p-1.5 text-white/45 hover:text-neon-pink" data-testid={`remove-staff-${staff.id}`}><Trash size={13} weight="bold" /></button>
      )}
    </div>
  );
}

function AddStaffSheet({ category, onClose, onAdded }) {
  const { notify } = useDialog();
  const [name, setName] = useState(""); const [phone, setPhone] = useState("");
  const [shift, setShift] = useState("DAY"); const [supervisor, setSupervisor] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/api/society-staff", {
        name: name.trim(), category,
        shift: category === "SECURITY" ? shift : null,
        isSupervisor: supervisor, phone: phone || null,
      });
      onAdded();
    } catch { notify({ title: "Couldn't add", message: "Could not add the personnel. Please try again." }); }
    finally { setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe" data-testid="add-staff-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Add {category === "SECURITY" ? "security" : "housekeeping"} staff</h3>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div><label className="label">Name</label>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="staff-name" /></div>
          <div><label className="label">Phone (optional)</label>
            <input className="field" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit" data-testid="staff-phone" /></div>
          {category === "SECURITY" && (
            <div><label className="label">Shift</label>
              <select className="field !py-3" value={shift} onChange={(e) => setShift(e.target.value)} data-testid="staff-shift">
                <option value="DAY" className="bg-ink-800">Day</option>
                <option value="NIGHT" className="bg-ink-800">Night</option>
              </select></div>
          )}
          <label className="flex items-center gap-2 text-[13px] text-white/80">
            <input type="checkbox" checked={supervisor} onChange={(e) => setSupervisor(e.target.checked)} data-testid="staff-supervisor" />
            <Crown size={14} weight="fill" className="text-neon-amber" /> Mark as supervisor
          </label>
          <button className="btn-primary w-full" disabled={busy || !name.trim()} data-testid="staff-submit">{busy ? "Adding…" : "Add staff"}</button>
        </div>
      </motion.form>
    </motion.div>
  );
}
